import React, { useMemo } from 'react';
import { Box, IconButton, Menu } from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { ActionDescriptor } from '@finance/contracts';
import { ActionRenderer } from '../ActionRenderer.tsx';
import type { EvaluationContext } from '../../../runtime/conditions/evaluate-condition.ts';
import { resolveActions, type ResolvedAction } from '../../../runtime/actions/action-resolution';
import { resolveTKey } from '../../../i18n/resolve-key';
import type { ActionExecutionContext } from '../../../runtime/actions/action-executor.ts';
import type { TableActionContext } from './table-renderer-props.ts';

interface TableRowActionsProps {
  actions: ActionDescriptor[];
  row: Record<string, unknown>;
  evalCtx: EvaluationContext;
  onAction?: (action: ResolvedAction, context: TableActionContext) => void | Promise<void>;
  /**
   * Number of actions displayed directly when a row has multiple actions.
   * The default keeps multi-action rows compact and exposes the full governed
   * action set from one keyboard-accessible overflow control.
   */
  overflowThreshold?: number;
}

export function TableRowActions({
  actions,
  row,
  evalCtx,
  onAction,
  overflowThreshold = 0,
}: TableRowActionsProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const popoverIdBase = React.useId();

  const resolved = useMemo(
    () =>
      resolveActions(actions, {
        ctx: evalCtx,
        data: row,
      }),
    [actions, evalCtx, row]
  );

  if (resolved.length === 0) return null;

  // A single action remains directly discoverable. Multiple actions collapse
  // by default so the sticky action column does not become a second toolbar.
  const directCount = resolved.length === 1 ? 1 : Math.max(0, overflowThreshold);
  const directActions = resolved.slice(0, directCount);
  const overflowActions = resolved.slice(directCount);
  const menuId = anchorEl ? `table-row-actions-${popoverIdBase}` : undefined;

  const handleResolvedAction = async (action: ResolvedAction, context?: ActionExecutionContext) => {
    try {
      await onAction?.(action, {
        source: 'row-action',
        row,
        confirmationHandled: context?.confirmationHandled,
        confirmationValues: context?.confirmationValues,
      });
    } finally {
      setAnchorEl(null);
    }
  };

  return (
    <Box
      data-table-interactive="true"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}
    >
      {directActions.map(action => (
        <ActionRenderer
          key={action.action.id}
          action={action}
          size="small"
          context={{ source: 'table-row', rowData: row }}
          onAction={handleResolvedAction}
        />
      ))}

      {overflowActions.length > 0 && (
        <>
          <IconButton
            data-testid="table-row-actions-menu-trigger"
            size="small"
            aria-label={t(resolveTKey('table.moreActions'))}
            aria-controls={menuId}
            aria-expanded={Boolean(anchorEl)}
            aria-haspopup="menu"
            onClick={event => setAnchorEl(event.currentTarget)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            data-testid="table-row-actions-menu"
            id={menuId}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { width: 240, maxWidth: 'calc(100vw - 32px)' } } }}
          >
            <Box component="div" aria-label={t(resolveTKey('table.moreActions'))}>
              {overflowActions.map(action => (
                <ActionRenderer
                  key={action.action.id}
                  action={action}
                  size="small"
                  fullWidth
                  menuItem
                  context={{ source: 'table-row', rowData: row }}
                  onAction={handleResolvedAction}
                />
              ))}
            </Box>
          </Menu>
        </>
      )}
    </Box>
  );
}
